import { getCustomerFacingPriceItem } from '@/helpers/customerPricing/customerPricing'

import { useCallback, useEffect, useMemo, useState } from 'react'
import type React from 'react'

import type { MenuItem } from '../../domain/menu-item'
import type { MenuVendor } from '../../domain/menu-vendor'
import type { AddItemPayload } from '../../states/menu-page/add-item-mutation'
import { useSharedMenuItemCardState } from './MenuItemCardContext'

export const useMenuItemCardOptimized = (menuItem: MenuItem, vendor: MenuVendor) => {
  const {
    account,
    t,
    sharedAddingItem,
    handleAddItemToBasket: sharedHandleAddItemToBasket,
  } = useSharedMenuItemCardState()

  const minQty = useMemo(() => {
    return menuItem.minQty && menuItem.minQty > 0 ? menuItem.minQty : 1
  }, [menuItem.minQty])

  const [quantity, setQuantity] = useState<number>(minQty)

  useEffect(() => {
    setQuantity((prev) => (prev < minQty ? minQty : prev))
  }, [minQty])

  const { servesAmount, price, priceCaveat } = useMemo(() => {
    const priceItem = getCustomerFacingPriceItem(menuItem.customerPriceItems)
    let serves = `${menuItem.servings}`
    let computedPrice = priceItem.priceExTax
    let caveat = ''

    if (menuItem.isPricePerHead) {
      serves = `${menuItem.minQty}${menuItem.minQty > 1 ? '+' : ''}`
      if (menuItem.minQty > 1) {
        computedPrice = computedPrice * menuItem.minQty
        caveat = `${t('marketplace:for', { minQty: menuItem.minQty })}`
      }
    }

    return { servesAmount: serves, price: computedPrice, priceCaveat: caveat }
  }, [
    menuItem.customerPriceItems,
    menuItem.isPricePerHead,
    menuItem.minQty,
    menuItem.priceExTax,
    menuItem.servings,
    t,
  ])

  const handleQuantityIncrease = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setQuantity((prev) => prev + 1)
  }, [])

  const handleQuantityDecrease = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setQuantity((prev) => Math.max(0, prev - 1))
  }, [])

  const handleAddItemToBasket = useCallback(
    (payload: AddItemPayload) => {
      sharedHandleAddItemToBasket(vendor, payload)
    },
    [sharedHandleAddItemToBasket, vendor],
  )

  const handleAddItem = useCallback(() => {
    if (quantity <= 0) return
    handleAddItemToBasket({ item: menuItem, qty: quantity })
    setQuantity(minQty)
  }, [quantity, handleAddItemToBasket, menuItem, minQty])

  const handleAddToCart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      handleAddItem()
    },
    [handleAddItem],
  )

  const quickAddDisabled = quantity <= minQty - 1

  return {
    quantity,
    account,
    minimumQuantity: minQty,

    servesAmount,
    price,
    priceCaveat,
    quickAddDisabled,

    handleQuantityIncrease,
    handleQuantityDecrease,
    handleAddItemToBasket,
    handleAddToCart,
    addingItem: sharedAddingItem,
  }
}
